import {Genre} from '../genres/genre';
import {Artist} from '../artists/artist';
import {Tag} from '@common/tags/tag';
import {Track} from '@app/web-player/tracks/track';
import { AlbumType, AlbumSubtype } from '@common/admin/use-album_taxonomies';
import {User} from '@ui/types/user';

export const ALBUM_MODEL = 'album';

export type DerivedSingleMode = 'inherit' | 'disabled' | 'custom';

export interface Album {
  id: number;
  name: string;
  model_type: typeof ALBUM_MODEL;
  release_date?: string;
  k_release_date?: string | undefined;
  spotify_id?: string;
  image?: string;
  image_format?: 'square' | 'landscape' | 'poster' | 'portrait';
  artwork?: string;
  artwork_large?: string;
  thumb_16_9?: string;
  thumb_1080?: string;
  thumb_4_5?: string;
  thumb_9_16?: string;
  artists?: Omit<Artist, 'albums'>[];
  reposts_count?: number;
  likes_count?: number;
  plays?: number;
  views: number;
  description?: string;
  tracks?: Track[];
  tags?: Tag[];
  genres?: Genre[];
  created_at?: string;
  owner_id?: number;
  comments_count?: number;
  tracks_count?: number;
  updated_at: string;
  type: AlbumType;
  subtype: AlbumSubtype;
  owner?: User;
  available?: boolean;
  encoded?: boolean;
  exclusivity?: boolean;
  is_searchable?: boolean;
  is_channel_visible?: boolean;
  auto_create_derived_singles?: boolean;
  is_derived?: boolean;
  derived_id?: number;
  derived_source_track_id?: number;
  derived_source_album_id?: number;
  derived_target_artist_id?: number;
}

export function isDerivedAlbum(album?: Album | null): boolean {
  return !!album?.is_derived;
}

export function getDerivedAlbumTrack(album?: Album | null): Track | undefined {
  if (!isDerivedAlbum(album)) {
    return undefined;
  }

  return album?.tracks?.[0];
}

export function getDerivedAlbumTrackLinkData(
  album?: Album | null,
): Pick<Track, 'id' | 'name'> | undefined {
  if (!isDerivedAlbum(album)) {
    return undefined;
  }

  const track = getDerivedAlbumTrack(album);
  if (track?.id) {
    return {
      id: track.id,
      name: track.name,
    };
  }

  if (album?.derived_source_track_id) {
    return {
      id: album.derived_source_track_id,
      name: album.name,
    };
  }

  return undefined;
}

export function getDerivedAlbumId(album?: Album | null): number | undefined {
  if (!isDerivedAlbum(album)) {
    return undefined;
  }

  if (album?.derived_id) {
    return album.derived_id;
  }

  if (typeof album?.id === 'number' && album.id !== 0) {
    return Math.abs(album.id);
  }

  return undefined;
}

export function getAlbumRouteAlbumId(album?: Album | null): number | undefined {
  if (isDerivedAlbum(album) && album?.derived_source_album_id) {
    return album.derived_source_album_id;
  }

  return album?.id;
}
